import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TEMA, useMobil } from '../theme';
import {
  Inköpsplan, PlanMånad, PlanPost, PlanKategori, PlanProducent, Säkerhet,
  hämtaInköpsplan, skapaPlanPost, ändraPlanPost, taBortPlanPost, startaInköpFrånPlan, sättInköpetsKategori, ändraInköpIPlanen,
  valutabelopp, felText, skapaProducent,
} from '../api/inkop';
import { sida, rubrik, etikett, kort, fält, knapp, knappSpök, länkknapp } from '../components/inkopStil';

// Inköpsplanen (beslut 29 okt 2026): tolv månader framåt i en överblick, en
// kolumn per månad. Varje producent är en lapp i sin månad, och stapeln
// ovanför visar ungefär vad månadens inköp kostar. Lappen fylls när inköpet är
// beställt, är ljusare i dialog och streckad när den bara är planerad, så att
// det syns att det mesta längre fram är grova avsikter.
//
// Ett klick på en månad visar den lite närmare under tavlan. Ett klick på en
// producent öppnar producent- eller kandidatkortet.

const RÖD = '#D9695F';

export const KATEGORI: Record<PlanKategori, { namn: string; kort: string; färg: string; emoji?: string }> = {
  glasvin: { namn: 'Glasvin', kort: 'Glasvin', färg: '#D98E8A' },
  lansering: { namn: 'Lanseringsvin', kort: 'Lansering', färg: '#6FB7B0' },
  unicorn: { namn: 'Unicorn', kort: 'Unicorn', färg: '#B49BE8', emoji: '🦄' },
};
const KATEGORIER: PlanKategori[] = ['glasvin', 'lansering', 'unicorn'];
const UTAN = '#8E8C85';
const SÄKERHET: Record<Säkerhet, string> = { bestallt: 'Beställt', dialog: 'I dialog', planerat: 'Planerat' };
const MÅNAD_LÅNG = ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december'];

const färgFör = (k: PlanKategori | null) => (k ? KATEGORI[k].färg : UTAN);

// Belopp i planen är grova. Tusental räcker, och miljoner med en decimal.
export function ungefär(v?: number | null): string {
  if (!v) return '–';
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toLocaleString('sv-SE', { maximumFractionDigits: 1 })} Mkr`;
  return `${Math.round(v / 1000).toLocaleString('sv-SE')} tkr`;
}
const heltal = (v?: number | null) => (v == null ? '–' : `${Math.round(v).toLocaleString('sv-SE')} kr`);

function alfa(hex: string, a: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

// Hur en lapp eller ett stapelsegment ser ut, efter hur säkert det är.
function yta(p: { kategori: PlanKategori | null; sakerhet: Säkerhet }): React.CSSProperties {
  const f = färgFör(p.kategori);
  if (p.sakerhet === 'bestallt') return { background: alfa(f, 0.85), border: `1px solid ${f}` };
  if (p.sakerhet === 'dialog') return { background: alfa(f, 0.14), border: `1.5px solid ${f}` };
  return {
    background: `repeating-linear-gradient(135deg, ${alfa(f, 0.28)} 0 3px, transparent 3px 7px)`,
    border: `1px dashed ${alfa(f, 0.75)}`,
  };
}

function Kategorimärke({ k, liten }: { k: PlanKategori | null; liten?: boolean }) {
  if (!k) return <span style={{ fontSize: '11px', color: TEMA.inkFaint }}>Ingen kategori</span>;
  const c = KATEGORI[k];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: liten ? '11px' : '12px', color: TEMA.inkSoft, whiteSpace: 'nowrap' }}>
      {c.emoji ? <span style={{ fontSize: liten ? '11px' : '13px' }}>{c.emoji}</span>
        : <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: c.färg }} />}
      {c.kort}
    </span>
  );
}

function producentlänk(p: PlanPost) {
  return `/inkop/producenter/${p.producent_id}${p.inkop_id ? `?inkop=${p.inkop_id}` : ''}`;
}

// ── Tavlan ───────────────────────────────────────────────────────────────────

// Planerade lappar och inköp utan bokad hämtning går att dra till en annan
// månad. Ett inköp med bokad hämtning ligger i hämtningens månad.
const flyttbar = (p: PlanPost) => p.typ === 'plan' || !p.har_hamtning;
const nyckel = (p: PlanPost) => `${p.typ}:${p.id}`;

function Lapp({ p, onDra, onKategori }: {
  p: PlanPost; onDra: (p: PlanPost | null) => void; onKategori: (p: PlanPost, var_: DOMRect) => void;
}) {
  const f = färgFör(p.kategori);
  const emoji = p.kategori ? KATEGORI[p.kategori].emoji : undefined;
  const kanFlyttas = flyttbar(p);
  return (
    <Link to={producentlänk(p)} draggable={kanFlyttas}
      onDragStart={e => { if (!kanFlyttas) return; e.dataTransfer.setData('text/plain', nyckel(p)); e.dataTransfer.effectAllowed = 'move'; onDra(p); }}
      onDragEnd={() => onDra(null)}
      title={`${p.producent_namn}${p.kandidat ? ' (kandidat)' : ''} · ${SÄKERHET[p.sakerhet]}${p.statusnamn && p.typ === 'inkop' ? ` (${p.statusnamn})` : ''}${p.belopp_kr ? ` · ca ${heltal(p.belopp_kr)}` : ''}${p.text ? `\n${p.text}` : ''}${kanFlyttas ? '\nDra till en annan månad för att flytta.' : '\nHämtningen är bokad. Flytta den i inköpet.'}`}
      style={{
        display: 'block', textDecoration: 'none', padding: '5px 7px 5px 9px', borderRadius: '3px', position: 'relative',
        background: p.sakerhet === 'bestallt' ? alfa(f, 0.26) : 'transparent',
        border: `1px ${p.sakerhet === 'planerat' ? 'dashed' : 'solid'} ${alfa(f, p.sakerhet === 'bestallt' ? 0.6 : 0.75)}`,
        cursor: kanFlyttas ? 'grab' : 'pointer',
      }}>
      <button type="button" aria-label="Välj kategori" title={p.kategori ? `${KATEGORI[p.kategori].namn}. Klicka för att byta.` : 'Välj kategori'}
        onClick={e => { e.preventDefault(); e.stopPropagation(); onKategori(p, e.currentTarget.getBoundingClientRect()); }}
        style={{
          position: 'absolute', top: '4px', right: '4px', width: '16px', height: '16px', padding: 0, borderRadius: '50%', cursor: 'pointer',
          border: `1px ${p.kategori ? 'solid' : 'dashed'} ${p.kategori ? f : TEMA.inkFaint}`, background: p.kategori ? alfa(f, 0.5) : 'transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', lineHeight: 1, color: TEMA.inkFaint,
        }}>{p.kategori ? '' : '+'}</button>
      {p.sakerhet === 'bestallt' && <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '3px', borderRadius: '3px 0 0 3px', background: f }} />}
      <span style={{ display: 'flex', alignItems: 'flex-start', gap: '4px', fontSize: '12px', color: p.sakerhet === 'planerat' ? TEMA.inkSoft : TEMA.ink, lineHeight: 1.3, paddingRight: '16px' }}>
        {emoji && <span style={{ fontSize: '11px' }}>{emoji}</span>}
        <span style={{ overflow: 'hidden', minWidth: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{p.producent_namn}</span>
      </span>
      <span style={{ display: 'block', fontFamily: "'IBM Plex Mono', monospace", fontSize: '10.5px', color: TEMA.inkFaint, marginTop: '1px' }}>
        {p.belopp_kr ? `${p.uppskattat ? '≈ ' : ''}${ungefär(p.belopp_kr)}` : '–'}{p.sakerhet !== 'bestallt' ? <span> · {p.sakerhet === 'dialog' ? 'dialog' : 'plan'}</span> : null}{p.sen ? <span style={{ color: RÖD }}> · sen</span> : null}
      </span>
    </Link>
  );
}

// Månadens belopp hopslaget per säkerhet och kategori, i den ordning de staplas.
function staplar(poster: PlanPost[]) {
  const ut: { sakerhet: Säkerhet; kategori: PlanKategori | null; belopp: number }[] = [];
  for (const s of ['bestallt', 'dialog', 'planerat'] as Säkerhet[]) {
    for (const k of [...KATEGORIER, null] as (PlanKategori | null)[]) {
      const belopp = poster.filter(p => p.sakerhet === s && p.kategori === k).reduce((a, p) => a + (p.belopp_kr || 0), 0);
      if (belopp > 0) ut.push({ sakerhet: s, kategori: k, belopp });
    }
  }
  return ut;
}

// Så många lappar visas per månad innan resten döljs bakom "+ N till".
const VISAS = 6;

function Kolumn({ m, max, vald, filter, dras, onVälj, onLäggTill, onSläpp, onDra, onKategori }: {
  m: PlanMånad; max: number; vald: boolean; filter: PlanKategori | null; dras: PlanPost | null;
  onVälj: () => void; onLäggTill: () => void; onSläpp: (nyckel: string) => void; onDra: (p: PlanPost | null) => void;
  onKategori: (p: PlanPost, var_: DOMRect) => void;
}) {
  const ärMobil = useMobil();
  const [över, setÖver] = useState(false);
  const [alla, setAlla] = useState(false);
  const poster = filter ? m.poster.filter(p => p.kategori === filter) : m.poster;
  const summa = poster.reduce((s, p) => s + (p.belopp_kr || 0), 0);
  const januari = m.namn === 'jan';
  const kanSläppa = dras && dras.manad !== m.manad;
  return (
    <div
      onDragOver={e => { if (kanSläppa) { e.preventDefault(); setÖver(true); } }}
      onDragLeave={() => setÖver(false)}
      onDrop={e => { e.preventDefault(); setÖver(false); const id = e.dataTransfer.getData('text/plain'); if (id && kanSläppa) onSläpp(id); }}
      style={{
        flex: ärMobil ? '1 0 118px' : '1 1 0', minWidth: ärMobil ? '118px' : '0', display: 'flex', flexDirection: 'column', padding: ärMobil ? '0 6px 10px' : '0 4px 10px',
        borderLeft: januari ? `1px solid ${TEMA.inkFaint}` : `1px solid ${TEMA.border}`,
        background: över ? alfa(TEMA.accent, 0.07) : vald ? 'rgba(255,255,255,0.025)' : 'transparent',
        transition: 'background 120ms',
      }}>
      <button onClick={onVälj} style={{
        background: 'none', border: 'none', padding: '10px 2px 8px', cursor: 'pointer', textAlign: 'left',
        borderBottom: `2px solid ${vald ? TEMA.accent : 'transparent'}`,
      }}>
        <span style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
          <span style={{ fontFamily: "'Fraunces', serif", fontSize: '17px', color: m.nu || vald ? TEMA.ink : TEMA.inkSoft, textTransform: 'capitalize' }}>{m.namn}</span>
          {(januari || m.nu) && <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '10.5px', color: TEMA.inkFaint }}>{m.ar}</span>}
          {m.nu && <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '10px', color: TEMA.accent, marginLeft: 'auto' }}>nu</span>}
        </span>
        <span style={{ display: 'block', fontFamily: "'IBM Plex Mono', monospace", fontSize: '12px', color: summa ? TEMA.ink : TEMA.inkFaint, marginTop: '2px' }}>
          {summa ? `≈ ${ungefär(summa)}` : '–'}
        </span>
      </button>

      {/* Stapeln: en bit per kategori och säkerhet, det säkraste nederst. */}
      <div style={{ height: '104px', display: 'flex', flexDirection: 'column-reverse', justifyContent: 'flex-start', padding: '10px 14% 0', gap: '2px', cursor: 'pointer' }} onClick={onVälj}>
        {staplar(poster).map(b => (
          <div key={`${b.sakerhet}-${b.kategori}`} title={`${b.kategori ? KATEGORI[b.kategori].namn : 'Ingen kategori'} · ${SÄKERHET[b.sakerhet].toLowerCase()} · ca ${heltal(b.belopp)}`}
            style={{ ...yta(b), height: `${Math.max(3, (b.belopp / max) * 92)}px`, borderRadius: '2px', boxSizing: 'border-box', flexShrink: 0 }} />
        ))}
        {!summa && <div style={{ height: '1px', background: TEMA.border }} />}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '10px', flex: 1 }}>
        {(alla ? poster : poster.slice(0, VISAS)).map(p => <Lapp key={nyckel(p)} p={p} onDra={onDra} onKategori={onKategori} />)}
        {poster.length > VISAS && (
          <button onClick={() => (alla ? setAlla(false) : (setAlla(true), onVälj()))} style={{
            background: 'none', border: 'none', color: TEMA.inkSoft, fontSize: '12px', padding: '4px 2px', cursor: 'pointer',
            fontFamily: "'Inter', sans-serif", textAlign: 'left',
          }}>{alla ? 'Visa färre' : `+ ${poster.length - VISAS} till`}</button>
        )}
        <button onClick={onLäggTill} title="Lägg till en producent i den här månaden" style={{
          background: 'none', border: `1px dashed ${TEMA.border}`, borderRadius: '3px', color: TEMA.inkFaint,
          fontSize: '12px', padding: '4px', cursor: 'pointer', fontFamily: "'Inter', sans-serif", marginTop: poster.length ? '2px' : 0,
        }}>+</button>
      </div>
    </div>
  );
}

// ── Formulär för en post ─────────────────────────────────────────────────────

// ny_namn och ny_land: en ny kandidat som skapas när posten sparas.
interface Utkast { producent_id: string; manad: string; kategori: PlanKategori | null; belopp: string; text: string; ny_namn?: string; ny_land?: string }

// Sökbart val av producent eller kandidat. Finns namnet inte kan det läggas
// till som ny kandidat direkt härifrån.
function Producentval({ producenter, värde, nyNamn, onVälj, onNy }: {
  producenter: PlanProducent[]; värde: string; nyNamn?: string;
  onVälj: (id: string) => void; onNy: (namn: string) => void;
}) {
  const vald = producenter.find(p => p.id === värde);
  const [sök, setSök] = useState(vald?.namn || nyNamn || '');
  const [öppen, setÖppen] = useState(false);
  const enkel = (t: string) => t.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const träffar = sök.trim() ? producenter.filter(p => enkel(p.namn).includes(enkel(sök.trim()))) : producenter;
  const exakt = producenter.some(p => enkel(p.namn) === enkel(sök.trim()));
  const rad: React.CSSProperties = {
    display: 'flex', justifyContent: 'space-between', gap: '10px', width: '100%', textAlign: 'left', background: 'none', border: 'none',
    padding: '7px 10px', cursor: 'pointer', fontFamily: "'Inter', sans-serif", fontSize: '13px', color: TEMA.ink,
  };
  return (
    <div style={{ position: 'relative' }}>
      <input style={fält} value={sök} autoFocus placeholder="Sök, eller skriv namnet på en ny kandidat"
        onFocus={() => setÖppen(true)} onBlur={() => setTimeout(() => setÖppen(false), 150)}
        onChange={e => { setSök(e.target.value); setÖppen(true); if (värde || nyNamn) onVälj(''); }} />
      {öppen && (
        <div style={{
          position: 'absolute', zIndex: 10, top: 'calc(100% + 4px)', left: 0, right: 0, maxHeight: '260px', overflowY: 'auto',
          background: TEMA.bgPanel, border: `1px solid ${TEMA.border}`, borderRadius: '4px', boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
        }}>
          {sök.trim() && !exakt && (
            <button type="button" style={{ ...rad, color: TEMA.accent, borderBottom: `1px solid ${TEMA.border}` }}
              onMouseDown={e => e.preventDefault()} onClick={() => { onNy(sök.trim()); setÖppen(false); }}>
              + Lägg till "{sök.trim()}" som ny kandidat
            </button>
          )}
          {träffar.slice(0, 40).map(p => (
            <button key={p.id} type="button" style={rad} onMouseDown={e => e.preventDefault()}
              onClick={() => { onVälj(p.id); setSök(p.namn); setÖppen(false); }}>
              <span>{p.namn}</span>
              <span style={{ color: TEMA.inkFaint, fontSize: '12px', whiteSpace: 'nowrap' }}>{[p.land, p.kandidat ? 'kandidat' : null].filter(Boolean).join(' · ')}</span>
            </button>
          ))}
          {träffar.length === 0 && !sök.trim() && <div style={{ ...rad, color: TEMA.inkFaint, cursor: 'default' }}>Inga producenter än</div>}
        </div>
      )}
    </div>
  );
}

function Kategorival({ värde, onVälj }: { värde: PlanKategori | null; onVälj: (k: PlanKategori | null) => void }) {
  return (
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
      {KATEGORIER.map(k => {
        const c = KATEGORI[k];
        const på = värde === k;
        return (
          <button key={k} type="button" onClick={() => onVälj(på ? null : k)} style={{
            padding: '6px 11px', borderRadius: '14px', cursor: 'pointer', fontFamily: "'Inter', sans-serif", fontSize: '12.5px',
            border: `1px solid ${på ? c.färg : TEMA.border}`, background: på ? alfa(c.färg, 0.18) : 'transparent',
            color: på ? TEMA.ink : TEMA.inkSoft, display: 'inline-flex', alignItems: 'center', gap: '6px',
          }}>
            {c.emoji ? <span>{c.emoji}</span> : <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: c.färg }} />}
            {c.namn}
          </button>
        );
      })}
    </div>
  );
}

function PostFormulär({ start, månader, producenter, nyPost, onSpara, onAvbryt }: {
  start: Utkast; månader: PlanMånad[]; producenter: PlanProducent[]; nyPost: boolean;
  onSpara: (u: Utkast) => Promise<void>; onAvbryt: () => void;
}) {
  const [u, setU] = useState<Utkast>(start);
  const [sparar, setSparar] = useState(false);
  const vald = producenter.find(p => p.id === u.producent_id);
  function väljProducent(id: string) {
    const p = producenter.find(x => x.id === id);
    setU(x => ({ ...x, producent_id: id, ny_namn: undefined, ny_land: undefined, kategori: x.kategori ?? p?.kategori ?? null }));
  }
  async function spara() {
    setSparar(true);
    try { await onSpara(u); } finally { setSparar(false); }
  }
  const etikettLiten: React.CSSProperties = { ...etikett, marginBottom: '4px' };
  return (
    <div style={{ border: `1px solid ${TEMA.border}`, borderRadius: '4px', padding: '14px', background: TEMA.bg, margin: '10px 0' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
        {nyPost && (
          <div>
            <span style={etikettLiten}>Producent eller kandidat</span>
            <Producentval producenter={producenter} värde={u.producent_id} nyNamn={u.ny_namn}
              onVälj={väljProducent} onNy={namn => setU(x => ({ ...x, producent_id: '', ny_namn: namn }))} />
          </div>
        )}
        {nyPost && u.ny_namn && (
          <label>
            <span style={etikettLiten}>Land för den nya kandidaten</span>
            <input style={fält} value={u.ny_land || ''} placeholder="t.ex. Frankrike" onChange={e => setU({ ...u, ny_land: e.target.value })} />
          </label>
        )}
        <label>
          <span style={etikettLiten}>Månad</span>
          <select style={fält} value={u.manad} onChange={e => setU({ ...u, manad: e.target.value })}>
            {månader.map(m => <option key={m.manad} value={m.manad}>{MÅNAD_LÅNG[Number(m.manad.slice(5, 7)) - 1]} {m.ar}</option>)}
          </select>
        </label>
        <label>
          <span style={etikettLiten}>Ungefär, kr exkl. moms</span>
          <input style={fält} inputMode="numeric" value={u.belopp}
            placeholder={vald?.forslag_kr ? `förra inköpet ≈ ${Math.round(vald.forslag_kr).toLocaleString('sv-SE')}` : 't.ex. 80 000'}
            onChange={e => setU({ ...u, belopp: e.target.value })} />
          {nyPost && vald?.forslag_kr && !u.belopp && (
            <button type="button" style={{ ...länkknapp, fontSize: '12px', marginTop: '4px' }}
              onClick={() => setU({ ...u, belopp: String(Math.round(vald.forslag_kr!)) })}>
              Samma som förra gången
            </button>
          )}
        </label>
      </div>
      <div style={{ marginTop: '12px' }}>
        <span style={etikettLiten}>Kategori</span>
        <Kategorival värde={u.kategori} onVälj={k => setU({ ...u, kategori: k })} />
      </div>
      <label style={{ display: 'block', marginTop: '12px' }}>
        <span style={etikettLiten}>Anteckning</span>
        <input style={fält} value={u.text} placeholder="t.ex. rosé inför våren" onChange={e => setU({ ...u, text: e.target.value })} />
      </label>
      <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
        <button style={{ ...knapp, opacity: sparar || !(u.producent_id || u.ny_namn) ? 0.6 : 1 }} disabled={sparar || !(u.producent_id || u.ny_namn)} onClick={spara}>
          {nyPost ? (u.ny_namn ? 'Skapa kandidaten och lägg i planen' : 'Lägg i planen') : 'Spara'}
        </button>
        <button style={knappSpök} onClick={onAvbryt}>Avbryt</button>
      </div>
    </div>
  );
}

const tillBelopp = (s: string): number | null => {
  const n = Number(s.replace(/\s/g, '').replace(',', '.'));
  return s.trim() && !Number.isNaN(n) ? n : null;
};

// ── Månaden närmare ──────────────────────────────────────────────────────────

function Fördelning({ m }: { m: PlanMånad }) {
  const delar = [...KATEGORIER.map(k => ({ k: k as PlanKategori | null, v: m.per_kategori[k] })),
    { k: null, v: m.totalt - KATEGORIER.reduce((s, k) => s + m.per_kategori[k], 0) }].filter(d => d.v > 0);
  if (!m.totalt) return null;
  return (
    <div style={{ marginTop: '12px' }}>
      <div style={{ display: 'flex', height: '6px', borderRadius: '3px', overflow: 'hidden', gap: '2px' }}>
        {delar.map(d => <div key={d.k || 'utan'} style={{ flex: d.v, background: färgFör(d.k) }} />)}
      </div>
      <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginTop: '7px', fontSize: '12px', color: TEMA.inkSoft }}>
        {delar.map(d => (
          <span key={d.k || 'utan'} style={{ display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
            <Kategorimärke k={d.k} liten /><span style={{ fontFamily: "'IBM Plex Mono', monospace", color: TEMA.ink }}>{ungefär(d.v)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function Rad({ p, månader, producenter, onKör }: {
  p: PlanPost; månader: PlanMånad[]; producenter: PlanProducent[];
  onKör: (f: () => Promise<unknown>, fel: string, efter?: (svar: any) => void) => Promise<void>;
}) {
  const ärMobil = useMobil();
  const navigate = useNavigate();
  const [ändrar, setÄndrar] = useState(false);
  const [väljKategori, setVäljKategori] = useState(false);
  const plan = p.typ === 'plan';

  if (ändrar && plan) {
    return (
      <PostFormulär nyPost={false} månader={månader} producenter={producenter}
        start={{ producent_id: p.producent_id, manad: p.manad, kategori: p.kategori, belopp: p.belopp_kr ? String(Math.round(p.belopp_kr)) : '', text: p.text || '' }}
        onAvbryt={() => setÄndrar(false)}
        onSpara={u => onKör(() => ändraPlanPost(p.id, { manad: u.manad, kategori: u.kategori, belopp_ca: tillBelopp(u.belopp), text: u.text }),
          'Kunde inte spara.', () => setÄndrar(false))} />
    );
  }

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: ärMobil ? '1fr auto' : '118px minmax(0, 1fr) auto auto', gap: ärMobil ? '6px 12px' : '14px',
      alignItems: 'center', padding: '10px 0', borderTop: `1px solid ${TEMA.border}`,
    }}>
      <div style={{ position: 'relative', gridColumn: ärMobil ? '1 / -1' : undefined }}>
        <button onClick={() => setVäljKategori(!väljKategori)} title="Byt kategori"
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
          <Kategorimärke k={p.kategori} />
        </button>
        {väljKategori && (
          <div style={{ position: 'absolute', zIndex: 5, top: '22px', left: 0, background: TEMA.bgPanel, border: `1px solid ${TEMA.border}`, borderRadius: '4px', padding: '10px', width: '330px', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
            <Kategorival värde={p.kategori} onVälj={k => {
              setVäljKategori(false);
              onKör(() => plan ? ändraPlanPost(p.id, { kategori: k }) : sättInköpetsKategori(p.inkop_id!, k), 'Kunde inte byta kategori.');
            }} />
          </div>
        )}
      </div>
      <div style={{ minWidth: 0 }}>
        <Link to={producentlänk(p)} style={{ color: TEMA.ink, textDecoration: 'none', fontSize: '14px' }}>
          {p.producent_namn}
        </Link>
        {p.kandidat && <span style={{ fontSize: '11px', color: TEMA.inkFaint, marginLeft: '8px', fontFamily: "'IBM Plex Mono', monospace" }}>kandidat</span>}
        <div style={{ fontSize: '12px', color: TEMA.inkFaint, marginTop: '2px' }}>
          {[
            plan ? (p.sen && p.planerad_manad ? `Planerat till ${MÅNAD_LÅNG[Number(p.planerad_manad.slice(5, 7)) - 1]}, inget inköp än` : 'Planerat, inget inköp än') : p.statusnamn,
            !plan && p.utan_datum ? 'varken hämtning eller beställningsdag' : null,
            !plan && p.sen ? 'skulle ha hämtats tidigare' : null,
            p.flaskor ? `${p.flaskor.toLocaleString('sv-SE')} fl` : null,
            p.text,
          ].filter(Boolean).join(' · ')}
        </div>
      </div>
      <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '13px', color: p.belopp_kr ? TEMA.ink : TEMA.inkFaint }}>
          {p.belopp_kr ? `${p.uppskattat ? '≈ ' : ''}${heltal(p.belopp_kr)}` : 'Belopp saknas'}
        </div>
        {!plan && p.valuta && p.valuta !== 'SEK' && p.belopp_valuta ? (
          <div style={{ fontSize: '11.5px', color: TEMA.inkFaint }}>{valutabelopp(p.belopp_valuta, p.valuta, 0)}</div>
        ) : null}
      </div>
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', flexWrap: 'wrap', gridColumn: ärMobil ? '1 / -1' : undefined }}>
        {plan ? (
          <>
            <button style={länkknapp} onClick={() => onKör(() => startaInköpFrånPlan(p.id), 'Kunde inte starta inköpet.',
              svar => navigate(`/inkop/producenter/${p.producent_id}?inkop=${svar?.data?.id || ''}`))}>Starta inköp</button>
            <button style={{ ...länkknapp, color: TEMA.inkSoft }} onClick={() => setÄndrar(true)}>Ändra</button>
            <button style={{ ...länkknapp, color: TEMA.inkFaint }} onClick={() => {
              if (window.confirm(`Ta bort ${p.producent_namn} från planen?`)) onKör(() => taBortPlanPost(p.id), 'Kunde inte ta bort.');
            }}>Ta bort</button>
          </>
        ) : (
          <Link to={`/inkop/lista/${p.inkop_id}`} style={{ ...länkknapp, textDecoration: 'none' }}>Öppna inköpet →</Link>
        )}
      </div>
    </div>
  );
}

function Månaden({ m, plan, filter, onKör, nyPost, setNyPost }: {
  m: PlanMånad; plan: Inköpsplan; filter: PlanKategori | null;
  onKör: (f: () => Promise<unknown>, fel: string, efter?: (svar: any) => void) => Promise<void>;
  nyPost: boolean; setNyPost: (v: boolean) => void;
}) {
  const poster = filter ? m.poster.filter(p => p.kategori === filter) : m.poster;
  const titel = `${MÅNAD_LÅNG[Number(m.manad.slice(5, 7)) - 1]} ${m.ar}`;
  return (
    <div style={{ ...kort, marginTop: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: '22px', color: TEMA.ink, margin: 0, fontWeight: 400, textTransform: 'capitalize' }}>{titel}</h2>
          <div style={{ fontSize: '12.5px', color: TEMA.inkSoft, marginTop: '4px' }}>
            {m.totalt ? <>Ungefär <span style={{ color: TEMA.ink, fontFamily: "'IBM Plex Mono', monospace" }}>{heltal(m.totalt)}</span> exkl. moms</> : 'Inget planerat än'}
            {m.totalt > 0 && (['bestallt', 'dialog', 'planerat'] as Säkerhet[]).filter(s => m.summa[s]).map(s => (
              <span key={s}> · {SÄKERHET[s].toLowerCase()} {ungefär(m.summa[s])}</span>
            ))}
          </div>
        </div>
        {!nyPost && <button style={knappSpök} onClick={() => setNyPost(true)}>+ Lägg till i {MÅNAD_LÅNG[Number(m.manad.slice(5, 7)) - 1]}</button>}
      </div>
      {!filter && <Fördelning m={m} />}
      {nyPost && (
        <PostFormulär nyPost månader={plan.manader} producenter={plan.producenter}
          start={{ producent_id: '', manad: m.manad, kategori: filter, belopp: '', text: '' }}
          onAvbryt={() => setNyPost(false)}
          onSpara={u => onKör(async () => {
            let producent_id = u.producent_id;
            if (!producent_id && u.ny_namn) {
              const ny = await skapaProducent({ namn: u.ny_namn, land: u.ny_land?.trim() || null });
              producent_id = ny.data.id;
            }
            return skapaPlanPost({ producent_id, manad: u.manad, kategori: u.kategori, belopp_ca: tillBelopp(u.belopp), text: u.text });
          },
            'Kunde inte lägga till.', () => setNyPost(false))} />
      )}
      <div style={{ marginTop: '14px' }}>
        {poster.length === 0 && !nyPost && (
          <p style={{ fontSize: '13px', margin: 0, color: TEMA.inkFaint }}>
            {filter ? `Inga ${KATEGORI[filter].namn.toLowerCase()} den här månaden.` : 'Inga inköp eller planer den här månaden.'}
          </p>
        )}
        {poster.map(p => <Rad key={`${p.typ}-${p.id}`} p={p} månader={plan.manader} producenter={plan.producenter} onKör={onKör} />)}
      </div>
    </div>
  );
}

// Beställt tidigare men inte markerat som inlevererat. Räknas inte i planen.
function Äldre({ plan, onKör, onStäng }: {
  plan: Inköpsplan; onStäng: () => void;
  onKör: (f: () => Promise<unknown>, fel: string, efter?: (svar: any) => void) => Promise<void>;
}) {
  return (
    <div style={{ ...kort, marginBottom: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: '22px', color: TEMA.ink, margin: 0, fontWeight: 400 }}>Äldre inköp som inte är avslutade</h2>
          <div style={{ fontSize: '12.5px', color: TEMA.inkSoft, marginTop: '4px', maxWidth: '640px' }}>
            Beställda före {MÅNAD_LÅNG[Number(plan.manader[0].manad.slice(5, 7)) - 1]}, eller utan något datum, men inte markerade som inlevererade.
            De räknas inte in i planen. Har vinet kommit, öppna inköpet och flytta det till Inlevererat.
          </div>
        </div>
        <button style={knappSpök} onClick={onStäng}>Stäng</button>
      </div>
      <div style={{ marginTop: '14px' }}>
        {plan.aldre.map(p => <Rad key={p.id} p={p} månader={plan.manader} producenter={plan.producenter} onKör={onKör} />)}
      </div>
    </div>
  );
}

// ── Sidan ────────────────────────────────────────────────────────────────────

export default function InkopPlan() {
  const ärMobil = useMobil();
  const [plan, setPlan] = useState<Inköpsplan | null>(null);
  const [fel, setFel] = useState<string | null>(null);
  const [vald, setVald] = useState<string | null>(null);
  const [filter, setFilter] = useState<PlanKategori | null>(null);
  const [nyPost, setNyPost] = useState(false);
  const [dras, setDras] = useState<PlanPost | null>(null);

  const ladda = useCallback(() => hämtaInköpsplan()
    .then(r => { setPlan(r.data); setFel(null); })
    .catch(e => setFel(felText(e, 'Kunde inte hämta inköpsplanen.'))), []);
  useEffect(() => { ladda(); }, [ladda]);

  async function kör(f: () => Promise<unknown>, meddelande: string, efter?: (svar: any) => void) {
    try {
      const svar = await f();
      await ladda();
      efter?.(svar);
    } catch (err) {
      setFel(felText(err, meddelande));
    }
  }

  const månad = useMemo(() => vald === 'aldre' ? null : plan?.manader.find(m => m.manad === vald) || plan?.manader[0] || null, [plan, vald]);
  const max = useMemo(() => Math.max(1, ...(plan?.manader || []).map(m =>
    (filter ? m.poster.filter(p => p.kategori === filter) : m.poster).reduce((s, p) => s + (p.belopp_kr || 0), 0))), [plan, filter]);

  function flytta(n: string, till: string) {
    if (!plan) return;
    const lapp = plan.manader.flatMap(x => x.poster).find(p => nyckel(p) === n);
    if (!lapp || !flyttbar(lapp)) return;
    // Lappen flyttas direkt, och planen hämtas om efteråt.
    setPlan({
      ...plan, manader: plan.manader.map(m => {
        const utan = m.poster.filter(p => nyckel(p) !== n);
        return m.manad === till ? { ...m, poster: [...utan, { ...lapp, manad: till, sen: false }] } : { ...m, poster: utan };
      }),
    });
    kör(() => lapp.typ === 'plan' ? ändraPlanPost(lapp.id, { manad: till }) : ändraInköpIPlanen(lapp.inkop_id!, { manad: till }), 'Kunde inte flytta.');
  }

  // Kategorin väljs direkt på lappen, i en ruta bredvid pricken.
  const [kategoriFör, setKategoriFör] = useState<{ p: PlanPost; x: number; y: number } | null>(null);
  function väljKategori(p: PlanPost, k: PlanKategori | null) {
    setKategoriFör(null);
    kör(() => p.typ === 'plan' ? ändraPlanPost(p.id, { kategori: k }) : sättInköpetsKategori(p.inkop_id!, k), 'Kunde inte byta kategori.');
  }
  useEffect(() => {
    if (!kategoriFör) return;
    const stäng = () => setKategoriFör(null);
    window.addEventListener('scroll', stäng, true);
    return () => window.removeEventListener('scroll', stäng, true);
  }, [kategoriFör]);

  // Tolv kolumner i bredd när de får plats, annars två rader om sex, så att
  // hela året alltid syns utan att man scrollar i sidled.
  const tavla = useRef<HTMLDivElement>(null);
  const [bredd, setBredd] = useState(1200);
  const laddad = plan !== null;
  useEffect(() => {
    const el = tavla.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setBredd(el.clientWidth));
    ro.observe(el);
    setBredd(el.clientWidth);
    return () => ro.disconnect();
  }, [laddad]);
  const rader = useMemo(() => {
    const m = plan?.manader || [];
    return ärMobil || bredd / 12 >= 96 ? [m] : [m.slice(0, 6), m.slice(6)];
  }, [plan, bredd, ärMobil]);

  const tot = plan ? (filter ? plan.per_kategori[filter] : plan.totalt) : 0;

  return (
    <div style={sida}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '16px', flexWrap: 'wrap', marginBottom: '18px' }}>
        <div>
          <span style={etikett}>INKÖP · TOLV MÅNADER FRAMÅT</span>
          <h1 style={rubrik}>Inköpsplan</h1>
        </div>
        {plan && (
          <div style={{ textAlign: ärMobil ? 'left' : 'right' }}>
            <span style={{ ...etikett, marginBottom: '2px' }}>{filter ? KATEGORI[filter].namn.toUpperCase() : 'UNGEFÄR, EXKL. MOMS'}</span>
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: '26px', color: TEMA.ink }}>≈ {ungefär(tot)}</div>
            {!filter && plan.totalt > 0 && (
              <div style={{ fontSize: '12px', color: TEMA.inkFaint }}>
                beställt {ungefär(plan.per_sakerhet.bestallt)} · i dialog {ungefär(plan.per_sakerhet.dialog)} · planerat {ungefär(plan.per_sakerhet.planerat)}
              </div>
            )}
          </div>
        )}
      </div>

      {fel && <p style={{ color: RÖD, fontSize: '13px' }}>{fel}</p>}
      {!plan && !fel && <p>Laddar...</p>}

      {plan && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <button onClick={() => setFilter(null)} style={{
                padding: '5px 12px', borderRadius: '14px', cursor: 'pointer', fontFamily: "'Inter', sans-serif", fontSize: '12.5px',
                border: `1px solid ${!filter ? TEMA.inkSoft : TEMA.border}`, background: !filter ? 'rgba(255,255,255,0.05)' : 'transparent',
                color: !filter ? TEMA.ink : TEMA.inkSoft,
              }}>Alla</button>
              {KATEGORIER.map(k => {
                const c = KATEGORI[k];
                const på = filter === k;
                return (
                  <button key={k} onClick={() => setFilter(på ? null : k)} style={{
                    padding: '5px 12px', borderRadius: '14px', cursor: 'pointer', fontFamily: "'Inter', sans-serif", fontSize: '12.5px',
                    border: `1px solid ${på ? c.färg : TEMA.border}`, background: på ? alfa(c.färg, 0.16) : 'transparent',
                    color: på ? TEMA.ink : TEMA.inkSoft, display: 'inline-flex', alignItems: 'center', gap: '6px',
                  }}>
                    {c.emoji ? <span>{c.emoji}</span> : <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: c.färg }} />}
                    {c.namn}
                    <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', color: TEMA.inkFaint }}>{ungefär(plan.per_kategori[k])}</span>
                  </button>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: '14px', fontSize: '11.5px', color: TEMA.inkFaint, alignItems: 'center' }}>
              {(['bestallt', 'dialog', 'planerat'] as Säkerhet[]).map(s => (
                <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ ...yta({ kategori: null, sakerhet: s }), width: '16px', height: '11px', borderRadius: '2px', boxSizing: 'border-box', display: 'inline-block' }} />
                  {SÄKERHET[s]}
                </span>
              ))}
            </div>
          </div>

          {plan.aldre.length > 0 && (
            <button onClick={() => { setVald(vald === 'aldre' ? null : 'aldre'); setNyPost(false); }} style={{
              display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', gap: '12px', textAlign: 'left',
              background: 'transparent', border: `1px solid ${TEMA.border}`, borderRadius: '4px', padding: '9px 14px', marginBottom: '10px',
              cursor: 'pointer', fontFamily: "'Inter', sans-serif", fontSize: '12.5px', color: TEMA.inkSoft,
            }}>
              <span>
                <span style={{ color: TEMA.ink }}>{plan.aldre.length} äldre {plan.aldre.length === 1 ? 'inköp är' : 'inköp är'} beställda men inte inlevererade</span>
                {' '}· ≈ {ungefär(plan.aldre_totalt)}, räknas inte i planen
              </span>
              <span style={{ color: TEMA.accent, whiteSpace: 'nowrap' }}>{vald === 'aldre' ? 'Dölj' : 'Visa'}</span>
            </button>
          )}

          {vald === 'aldre' && <div style={{ marginBottom: '16px' }}><Äldre plan={plan} onKör={kör} onStäng={() => setVald(null)} /></div>}

          {ärMobil && (
            <p style={{ fontSize: '12px', color: TEMA.inkFaint, margin: '0 0 8px' }}>Svep i sidled för hela året →</p>
          )}
          <div ref={tavla} style={{ ...kort, padding: 0, overflowX: ärMobil ? 'auto' : 'hidden', marginBottom: 0 }}>
            {rader.map((rad, n) => (
              <div key={n} style={{ display: 'flex', minWidth: ärMobil ? `${12 * 124}px` : undefined, borderTop: n ? `1px solid ${TEMA.border}` : undefined }}>
                {rad.map(m => (
                  <Kolumn key={m.manad} m={m} max={max} vald={månad?.manad === m.manad} filter={filter} dras={dras}
                    onVälj={() => { setVald(m.manad); setNyPost(false); }}
                    onLäggTill={() => { setVald(m.manad); setNyPost(true); }}
                    onSläpp={n => flytta(n, m.manad)} onDra={setDras}
                    onKategori={(p, r) => setKategoriFör({ p, x: Math.min(r.left, window.innerWidth - 360), y: r.bottom + 6 })} />
                ))}
              </div>
            ))}
          </div>
          <p style={{ fontSize: '11.5px', color: TEMA.inkFaint, margin: '8px 2px 0' }}>
            Ju längre fram, desto mer är bara planerat och desto grövre är beloppen. Ett inköp hamnar i månaden det hämtas.
            Lappar kan dras till en annan månad, utom inköp med bokad hämtning. Pricken på lappen väljer kategori.
          </p>

          {månad && <Månaden m={månad} plan={plan} filter={filter} onKör={kör} nyPost={nyPost} setNyPost={setNyPost} />}

          {kategoriFör && (
            <>
              <div onClick={() => setKategoriFör(null)} style={{ position: 'fixed', inset: 0, zIndex: 20 }} />
              <div style={{
                position: 'fixed', left: kategoriFör.x, top: kategoriFör.y, zIndex: 21, background: TEMA.bgPanel,
                border: `1px solid ${TEMA.border}`, borderRadius: '4px', padding: '10px 12px', boxShadow: '0 8px 24px rgba(0,0,0,0.45)', width: '340px',
              }}>
                <span style={{ ...etikett, marginBottom: '8px' }}>{kategoriFör.p.producent_namn}</span>
                <Kategorival värde={kategoriFör.p.kategori} onVälj={k => väljKategori(kategoriFör.p, k)} />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
